/**
 * HTE Warehouse Transfer Service
 * Frontend API wrapper for warehouse transfer CRUD operations.
 */
import api from './api';

// ============================================================================
// Types
// ============================================================================

export interface HteTransfer {
  transfer_number: string;
  transfer_date?: string;
  request_date?: string;
  operator_id?: string;
  memo?: string;
  request_by?: string;
  receive_by?: string;
  warehouse_code_from: string;
  location_code_from?: string;
  warehouse_code_to: string;
  location_code_to?: string;
  status: 'draft' | 'pending' | 'scanned' | 'complete' | 'cancelled';
  is_complete?: string;
  print_lock?: string;
  created_by?: string;
  updated_by?: string;
  completed_by?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Production planning fields
  source?: 'manual' | 'auto-generated' | 'daily-transfer-out';
  planned_for_date?: string;
  transfer_type?: 'standard' | 'daily_out';
  // Joined fields
  line_count?: number;
  details?: HteTransferDetail[];
}

export interface HteTransferDetail {
  id: number;
  transfer_number?: string;
  item_number: string;
  quantity_request?: number;
  quantity_shipped?: number;
  use_sub_unit?: string;
  is_frozen?: string;
  weight_shipped?: number;
  date_code?: string;
  remark?: string;
  sorting?: number;
  created_at: string;
  updated_at: string;
  // Additional fields (for display/future use)
  special_request?: string;
  avg_sold?: number;
  avg_back?: number;
  qs_type?: string;
  duration?: string;
  scan_complete?: string;
  qs_pend?: string;
  qs_rec_number?: string;
  salesperson_qs1?: string;
  q_item?: string;
  // Joined fields from ics.inventory
  item_english_name?: string;
  item_chinese_name?: string;
  item_unit?: string;
  item_sub_unit?: string;
  unit_weight?: number;
  sub_unit_weight?: number;
  packing?: string;
  english_brand_name?: string;
  chinese_brand_name?: string;
  // Nested weights
  weights: HteTransferWeightInfo[];
}

export interface HteTransferWeightInfo {
  id: number;
  transfer_detail_id: number;
  weight: number;
  created_at: string;
}

export interface InventorySearchItem {
  item_number: string;
  item_english_name?: string;
  item_chinese_name?: string;
  item_unit?: string;
  item_sub_unit?: string;
  unit_weight?: number;
  sub_unit_weight?: number;
  packing?: string;
  is_raw_material?: string;
  is_finish_product?: string;
  english_brand_name?: string;
  chinese_brand_name?: string;
  current_stock?: number;
}

export interface CreateTransferRequest {
  transfer_date?: string;
  request_date?: string;
  warehouse_code_from: string;
  location_code_from?: string;
  warehouse_code_to: string;
  location_code_to?: string;
  memo?: string;
  request_by?: string;
  receive_by?: string;
  created_by?: string;
}

export interface UpdateTransferRequest {
  transfer_date?: string;
  request_date?: string;
  warehouse_code_from?: string;
  location_code_from?: string;
  warehouse_code_to?: string;
  location_code_to?: string;
  memo?: string;
  request_by?: string;
  receive_by?: string;
  updated_by?: string;
}

export interface AddDetailRequest {
  item_number: string;
  quantity_request?: number;
  quantity_shipped?: number;
  use_sub_unit?: string;
  is_frozen?: string;
  weight_shipped?: number;
  date_code?: string;
  remark?: string;
  sorting?: number;
}

export interface UpdateDetailRequest extends Partial<AddDetailRequest> {}

export interface TransferCheckResult {
  itemNumber: string;
  requestedQuantity: number;
  locationCode: string;
  availableBalance: number;
  safetyQuantity: number;
  remainingAfterTransfer: number;
  isStockControlItem: boolean;
  decision: 'allowed' | 'capped' | 'out_of_stock';
  cappedQuantity: number | null;
  message: string | null;
}

export interface AddDetailResponse {
  data: HteTransferDetail;
  stockCheck?: TransferCheckResult;
}

export interface SubmitTransferResponse {
  status: string;
  message: string;
  promotedSubSessions: number;
  stockWarnings?: TransferCheckResult[];
}

export interface TransferReportRow {
  item_number: string;
  item_english_name?: string;
  item_chinese_name?: string;
  item_unit?: string;
  english_brand_name?: string;
  quantity_shipped?: number;
  weight_shipped?: number;
  use_sub_unit?: string;
  remark?: string;
  transfer_number: string;
  transfer_date?: string;
  warehouse_code_from: string;
  location_code_from?: string;
  warehouse_code_to: string;
  location_code_to?: string;
}

export interface TransferReportResponse {
  rows: TransferReportRow[];
  totals: {
    total_items: number;
    total_qty: number;
    total_weight: number;
  };
}

export interface ShipoutScanRecord {
  id: string;
  transfer_number: string;
  barcode: string;
  scanned_by: string;
  scanned_at: string;
  // Enriched from production_inventory
  item_number?: string;
  actual_weight?: number;
  box_number?: string;
  item_english_name?: string;
  item_chinese_name?: string;
}

export interface ShipoutScanSummaryItem {
  item_number: string;
  item_english_name?: string;
  category: 'finished_product' | 'remaining_material' | 'rejected_material' | 'by_product' | 'by_product_marketable' | 'by_product_production';
  remaining_type?: 'unused_lot' | 'remainder_intact' | 'remainder_not_intact';
  notes?: string;
  expected_quantity: number;
  expected_weight: number;
  scanned_count: number;
  scanned_weight: number;
  /** Whether this item has a scannable barcode. If false, use manual confirm. */
  has_barcode?: boolean;
}

export interface ShipoutScanResponse {
  scans: ShipoutScanRecord[];
  summary: ShipoutScanSummaryItem[];
  require_confirmation: boolean;
}

export interface TransfersResponse {
  transfers: HteTransfer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// ============================================================================
// Service
// ============================================================================

const BASE = '/inventory/hte-transfers';

export const hteTransferService = {
  // ---- Transfer Header ----

  async getTransfers(params?: {
    page?: number;
    limit?: number;
    status?: string;
    source?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Promise<TransfersResponse> {
    const response = await api.get(BASE, { params });
    return response.data.data;
  },

  async getTransfer(transferNumber: string): Promise<HteTransfer> {
    const response = await api.get(`${BASE}/${transferNumber}`);
    return response.data.data;
  },

  async createTransfer(data: CreateTransferRequest): Promise<HteTransfer> {
    const response = await api.post(BASE, data);
    return response.data.data;
  },

  async updateTransfer(transferNumber: string, data: UpdateTransferRequest): Promise<HteTransfer> {
    const response = await api.put(`${BASE}/${transferNumber}`, data);
    return response.data.data;
  },

  async cancelTransfer(transferNumber: string): Promise<HteTransfer> {
    const response = await api.delete(`${BASE}/${transferNumber}`);
    return response.data.data;
  },

  async completeTransfer(transferNumber: string, completed_by?: string): Promise<HteTransfer> {
    const response = await api.patch(`${BASE}/${transferNumber}/complete`, { completed_by });
    return response.data.data;
  },

  async submitTransfer(transferNumber: string, updated_by?: string): Promise<SubmitTransferResponse> {
    const response = await api.patch(`${BASE}/${transferNumber}/submit`, { updated_by });
    return response.data;
  },

  // ---- Transfer Details (Line Items) ----

  async addDetail(transferNumber: string, data: AddDetailRequest): Promise<AddDetailResponse> {
    const response = await api.post(`${BASE}/${transferNumber}/details`, data);
    return { data: response.data.data, stockCheck: response.data.stockCheck };
  },

  async updateDetail(transferNumber: string, detailId: number, data: UpdateDetailRequest): Promise<HteTransferDetail> {
    const response = await api.put(`${BASE}/${transferNumber}/details/${detailId}`, data);
    return response.data.data;
  },

  async removeDetail(transferNumber: string, detailId: number): Promise<void> {
    await api.delete(`${BASE}/${transferNumber}/details/${detailId}`);
  },

  // ---- Weight Info ----

  async getWeights(detailId: number): Promise<HteTransferWeightInfo[]> {
    const response = await api.get(`${BASE}/details/${detailId}/weights`);
    return response.data.data;
  },

  async addWeight(detailId: number, weight: number): Promise<HteTransferWeightInfo> {
    const response = await api.post(`${BASE}/details/${detailId}/weights`, { weight });
    return response.data.data;
  },

  async removeWeight(weightId: number): Promise<void> {
    await api.delete(`${BASE}/weights/${weightId}`);
  },

  // ---- Item Search ----

  async searchItems(query: string, limit?: number): Promise<InventorySearchItem[]> {
    const response = await api.get(`${BASE}/item-search`, {
      params: { q: query, limit: limit || 20 }
    });
    return response.data.data;
  },

  // ---- Report ----

  async getTransferReport(params: {
    date_from: string;
    date_to: string;
    item_from?: string;
    item_to?: string;
    brand?: string;
    warehouse_from?: string;
    location_from?: string;
    warehouse_to?: string;
    location_to?: string;
  }): Promise<TransferReportResponse> {
    const response = await api.get(`${BASE}/report`, { params });
    return response.data.data;
  },

  // ---- PDF ----

  async downloadPdf(transferNumber: string): Promise<Blob> {
    const response = await api.get(`${BASE}/${transferNumber}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ---- Ship-Out Scan Log ----

  async recordShipoutScan(transferNumber: string, barcode: string, scannedBy: string): Promise<ShipoutScanRecord> {
    const response = await api.post(`${BASE}/${transferNumber}/shipout-scan`, { barcode, scanned_by: scannedBy });
    return response.data.data;
  },

  async recordManualShipoutWeight(transferNumber: string, itemNumber: string, weight: number, scannedBy: string, remainingType?: string): Promise<ShipoutScanRecord> {
    const response = await api.post(`${BASE}/${transferNumber}/shipout-scan`, {
      item_number: itemNumber,
      manual_weight: weight,
      scanned_by: scannedBy,
      remaining_type: remainingType,
    });
    return response.data.data;
  },

  async confirmByProductPalette(transferNumber: string, scannedBy: string, itemNumber: string): Promise<ShipoutScanRecord[]> {
    const response = await api.post(`${BASE}/${transferNumber}/shipout-scan`, {
      confirm_byproduct: true,
      scanned_by: scannedBy,
      item_number: itemNumber,
    });
    return response.data.data;
  },

  async getShipoutScans(transferNumber: string): Promise<ShipoutScanResponse> {
    const response = await api.get(`${BASE}/${transferNumber}/shipout-scan`);
    return response.data.data;
  },

  async undoShipoutScan(transferNumber: string, scanId: string): Promise<void> {
    await api.delete(`${BASE}/${transferNumber}/shipout-scan/${scanId}`);
  },

  async clearShipoutScans(transferNumber: string): Promise<void> {
    await api.delete(`${BASE}/${transferNumber}/shipout-scan`);
  },

  async confirmShipoutScan(transferNumber: string, confirmedBy: string, requestBy?: string): Promise<void> {
    await api.patch(`${BASE}/${transferNumber}/shipout-scan/confirm`, { confirmed_by: confirmedBy, request_by: requestBy });
  },

  async getDraftInboundReminders(): Promise<any[]> {
    const response = await api.get(`${BASE}/draft-inbound-reminders`);
    return response.data.data;
  },
};

export default hteTransferService;
